/**
 * Provider Survey / Facility Information Sheet Component
 * Based on Child Care Centers: Certification Inspection Instrument (Renewal) Provider Survey
 * For documenting facility information required for licensing compliance
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import {
  Building2, FileText, Users, Clock, Car, Save, Printer,
  Download, History, CheckCircle, Calendar, MapPin, Phone, Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const AGE_GROUPS = [
  { id: "infant", label: "Infant (0-12 months)" },
  { id: "young_toddler", label: "Young Toddler (12-24 months)" },
  { id: "older_toddler", label: "Older Toddler (24-36 months)" },
  { id: "preschool", label: "Preschool (3-5 years)" },
  { id: "young_school_age", label: "Young School-Age (5-8 years)" },
  { id: "older_school_age", label: "Older School-Age (9-12 years)" }
];

export const ProviderSurveyForm = ({ centerName = "", organization = {}, onSave }) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form");

  // Form state
  const [formData, setFormData] = useState({
    // Facility Identification
    facilityName: centerName || organization?.name || "",
    facilityAddress: organization?.address || "",
    email: organization?.email || "",
    phone: organization?.phone || "",
    
    // Personnel
    currentDirector: "",
    designatedPerson: "",
    designeeHasAccess: "",
    
    // Capacity
    totalEnrolled: "",
    maxCapacity: "",
    
    // Age Groups
    ageGroups: [],
    
    // Hours of Operation
    operatingHours: DAYS_OF_WEEK.reduce((acc, day) => ({ ...acc, [day]: "" }), {}),
    monthsOfOperation: "all",
    specificMonths: "",
    
    // Program Activities
    administersMedication: "",
    servessMeals: "",
    hasPool: "",
    poolLocation: "",
    goesSwimming: "",
    goesOnWalks: "",
    walksLocation: "",
    goesOnFieldTrips: "",
    fieldTripsLocation: "",
    providesTransportation: "",
    numberOfVehicles: "",
    vehicleTypes: "",
    
    // Staffing
    staffEmployed: "",
    seasonalStaff: "",
    volunteers: "",
    facilityChanges: "",
    facilityChangesDescription: "",
    
    // Signature
    completedBy: "",
    title: "",
    signatureDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    if (centerName || organization?.name) {
      setFormData(prev => ({
        ...prev,
        facilityName: centerName || organization?.name || prev.facilityName,
        facilityAddress: organization?.address || prev.facilityAddress,
        email: organization?.email || prev.email,
        phone: organization?.phone || prev.phone
      }));
    }
  }, [centerName, organization]);

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/provider-surveys`);
      setSurveys(response.data || []);
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAgeGroupChange = (groupId, checked) => {
    setFormData(prev => ({
      ...prev,
      ageGroups: checked 
        ? [...prev.ageGroups, groupId]
        : prev.ageGroups.filter(g => g !== groupId)
    }));
  };

  const handleHoursChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: { ...prev.operatingHours, [day]: value }
    }));
  };

  const handleSave = async () => {
    if (!formData.facilityName || !formData.completedBy) {
      toast.error("Please fill in required fields: Facility Name and Completed By");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/provider-surveys`, formData);
      toast.success("Provider Survey saved successfully!");
      setSurveys(prev => [response.data, ...prev]);
      onSave?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save survey");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const loadPreviousSurvey = (survey) => {
    setFormData({
      ...survey,
      signatureDate: new Date().toISOString().split('T')[0]
    });
    setView("form");
    toast.success("Previous survey loaded - update details as needed");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-lg print:shadow-none print:border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between print:block">
            <div>
              <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
                <Building2 className="w-7 h-7" />
                Provider Survey / Facility Information Sheet
              </CardTitle>
              <CardDescription className="text-blue-600 mt-1">
                Child Care Centers: Certification Inspection Instrument (Renewal)
              </CardDescription>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setView(view === "form" ? "history" : "form")}
                className="border-blue-300"
              >
                {view === "form" ? <History className="w-4 h-4 mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                {view === "form" ? "History" : "Form"}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} className="border-blue-300">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>

        {view === "form" ? (
          <CardContent className="space-y-6">
            {/* Section 1: Facility Identification */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Facility Identification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Facility Name *</Label>
                  <Input
                    value={formData.facilityName}
                    onChange={(e) => handleChange("facilityName", e.target.value)}
                    className="mt-1"
                    placeholder="Enter facility name"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="mt-1"
                    placeholder="(XXX) XXX-XXXX"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-700">Facility Address</Label>
                  <Input
                    value={formData.facilityAddress}
                    onChange={(e) => handleChange("facilityAddress", e.target.value)}
                    className="mt-1"
                    placeholder="Street, City, State, ZIP"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="mt-1"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Personnel and Capacity */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Personnel and Capacity
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Current Director or Primary Staff Person</Label>
                  <Input
                    value={formData.currentDirector}
                    onChange={(e) => handleChange("currentDirector", e.target.value)}
                    className="mt-1"
                    placeholder="Director name"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Designated Person in Charge</Label>
                  <Input
                    value={formData.designatedPerson}
                    onChange={(e) => handleChange("designatedPerson", e.target.value)}
                    className="mt-1"
                    placeholder="Designee name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-700 block mb-2">Does Designee have access to all locked files when in charge?</Label>
                  <RadioGroup
                    value={formData.designeeHasAccess}
                    onValueChange={(v) => handleChange("designeeHasAccess", v)}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="access-yes" />
                      <Label htmlFor="access-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="access-no" />
                      <Label htmlFor="access-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-gray-700">Total # of children currently enrolled</Label>
                  <Input
                    type="number"
                    value={formData.totalEnrolled}
                    onChange={(e) => handleChange("totalEnrolled", e.target.value)}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Maximum # of children at any given time</Label>
                  <Input
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => handleChange("maxCapacity", e.target.value)}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Age Groups */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4">Ages of children in care (check all applicable)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {AGE_GROUPS.map(group => (
                  <div key={group.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={group.id}
                      checked={formData.ageGroups.includes(group.id)}
                      onCheckedChange={(checked) => handleAgeGroupChange(group.id, checked)}
                    />
                    <Label htmlFor={group.id} className="text-sm cursor-pointer">{group.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Hours of Operation */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Days and Hours of Operation
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day}>
                    <Label className="text-gray-700 text-sm">{day}</Label>
                    <Input
                      value={formData.operatingHours[day]}
                      onChange={(e) => handleHoursChange(day, e.target.value)}
                      className="mt-1"
                      placeholder="e.g., 7AM-6PM or N/A"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Months of Operation</Label>
                  <Select value={formData.monthsOfOperation} onValueChange={(v) => handleChange("monthsOfOperation", v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Year</SelectItem>
                      <SelectItem value="school_year">School Year Only</SelectItem>
                      <SelectItem value="summer">Summer Only</SelectItem>
                      <SelectItem value="specific">Specific Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.monthsOfOperation === "specific" && (
                  <div>
                    <Label className="text-gray-700">Specify Months</Label>
                    <Input
                      value={formData.specificMonths}
                      onChange={(e) => handleChange("specificMonths", e.target.value)}
                      className="mt-1"
                      placeholder="e.g., January - August"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section 5: Program Activities */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4">Programmatic Activities</h3>
              <div className="space-y-4">
                {/* Medication */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label className="text-gray-700">Does program administer medication?</Label>
                  <RadioGroup
                    value={formData.administersMedication}
                    onValueChange={(v) => handleChange("administersMedication", v)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="med-yes" />
                      <Label htmlFor="med-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="med-no" />
                      <Label htmlFor="med-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Meals */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label className="text-gray-700">Does program prepare and serve meals?</Label>
                  <RadioGroup
                    value={formData.servessMeals}
                    onValueChange={(v) => handleChange("servessMeals", v)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="meals-yes" />
                      <Label htmlFor="meals-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="meals-no" />
                      <Label htmlFor="meals-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Pool */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Is there a pool on site?</Label>
                    <RadioGroup
                      value={formData.hasPool}
                      onValueChange={(v) => handleChange("hasPool", v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="pool-yes" />
                        <Label htmlFor="pool-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="pool-no" />
                        <Label htmlFor="pool-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  {formData.hasPool === "yes" && (
                    <Input
                      value={formData.poolLocation}
                      onChange={(e) => handleChange("poolLocation", e.target.value)}
                      placeholder="If yes, where?"
                      className="mt-2"
                    />
                  )}
                </div>

                {/* Swimming */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label className="text-gray-700">Do children go swimming?</Label>
                  <RadioGroup
                    value={formData.goesSwimming}
                    onValueChange={(v) => handleChange("goesSwimming", v)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="swim-yes" />
                      <Label htmlFor="swim-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="swim-no" />
                      <Label htmlFor="swim-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Walks */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Do children go on walks off premises?</Label>
                    <RadioGroup
                      value={formData.goesOnWalks}
                      onValueChange={(v) => handleChange("goesOnWalks", v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="walks-yes" />
                        <Label htmlFor="walks-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="walks-no" />
                        <Label htmlFor="walks-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  {formData.goesOnWalks === "yes" && (
                    <Input
                      value={formData.walksLocation}
                      onChange={(e) => handleChange("walksLocation", e.target.value)}
                      placeholder="If yes, where?"
                      className="mt-2"
                    />
                  )}
                </div>

                {/* Field Trips */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Do children go on field trips?</Label>
                    <RadioGroup
                      value={formData.goesOnFieldTrips}
                      onValueChange={(v) => handleChange("goesOnFieldTrips", v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="trips-yes" />
                        <Label htmlFor="trips-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="trips-no" />
                        <Label htmlFor="trips-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  {formData.goesOnFieldTrips === "yes" && (
                    <Input
                      value={formData.fieldTripsLocation}
                      onChange={(e) => handleChange("fieldTripsLocation", e.target.value)}
                      placeholder="If yes, where?"
                      className="mt-2"
                    />
                  )}
                </div>

                {/* Transportation */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Is transportation provided?</Label>
                    <RadioGroup
                      value={formData.providesTransportation}
                      onValueChange={(v) => handleChange("providesTransportation", v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="trans-yes" />
                        <Label htmlFor="trans-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="trans-no" />
                        <Label htmlFor="trans-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  {formData.providesTransportation === "yes" && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Input
                        value={formData.numberOfVehicles}
                        onChange={(e) => handleChange("numberOfVehicles", e.target.value)}
                        placeholder="# of Vehicles"
                      />
                      <Input
                        value={formData.vehicleTypes}
                        onChange={(e) => handleChange("vehicleTypes", e.target.value)}
                        placeholder="Type(s) of Vehicle(s)"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 6: Staffing */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Staffing Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-gray-700"># of staff employed</Label>
                  <Input
                    type="number"
                    value={formData.staffEmployed}
                    onChange={(e) => handleChange("staffEmployed", e.target.value)}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-700"># of seasonal staff</Label>
                  <Input
                    type="number"
                    value={formData.seasonalStaff}
                    onChange={(e) => handleChange("seasonalStaff", e.target.value)}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-700"># of volunteers</Label>
                  <Input
                    type="number"
                    value={formData.volunteers}
                    onChange={(e) => handleChange("volunteers", e.target.value)}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-700 block mb-2">Any significant facility changes since last certificate?</Label>
                  <RadioGroup
                    value={formData.facilityChanges}
                    onValueChange={(v) => handleChange("facilityChanges", v)}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="changes-yes" />
                      <Label htmlFor="changes-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="changes-no" />
                      <Label htmlFor="changes-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                {formData.facilityChanges === "yes" && (
                  <div>
                    <Label className="text-gray-700">If yes, please describe:</Label>
                    <Textarea
                      value={formData.facilityChangesDescription}
                      onChange={(e) => handleChange("facilityChangesDescription", e.target.value)}
                      className="mt-1 min-h-[80px]"
                      placeholder="Describe facility changes..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section 7: Signature */}
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-4">Form Completion</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-700">Name of person completing form *</Label>
                  <Input
                    value={formData.completedBy}
                    onChange={(e) => handleChange("completedBy", e.target.value)}
                    className="mt-1"
                    placeholder="Print name"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    className="mt-1"
                    placeholder="e.g., Director, Owner"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Date</Label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => handleChange("signatureDate", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end print:hidden">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Survey
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        ) : (
          <CardContent>
            {/* History View */}
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : surveys.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No surveys saved yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {surveys.map((survey) => (
                  <div key={survey.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-800">{survey.facilityName}</span>
                        <Badge variant="outline" className="text-xs">
                          {new Date(survey.signatureDate).toLocaleDateString()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Completed by: {survey.completedBy} | Enrolled: {survey.totalEnrolled}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => loadPreviousSurvey(survey)}
                      className="text-blue-600 border-blue-300"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Load
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ProviderSurveyForm;
